import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useCart, FREE_SHIPPING_THRESHOLD } from "../context/CartContext";
import { api, imageUrl } from "../lib/api";
import { downloadDesign } from "../lib/designRender";
import { buildSizeRuns } from "../lib/sizeRuns";
import { Trash2, ArrowRight, Heart, Store, Download, Layers } from "lucide-react";

const TIP_OPTIONS = [2, 5, 10];

export default function Cart() {
  const { items, removeItem, updateQuantity, subtotal, shipping, tip, setTip, isPickup, setIsPickup, freeShipEligibleSubtotal } = useCart();
  const nav = useNavigate();

  // Server-authoritative money math (shipping + free-shipping progress) via /api/cart/quote.
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    if (items.length === 0) { setQuote(null); return; }
    let cancelled = false;
    const payload = {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.customization?.size || null })),
      pickup: isPickup,
    };
    api.post("/cart/quote", payload)
      .then((res) => { if (!cancelled) setQuote(res.data); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [items, isPickup]);

  const serverSubtotal = quote?.subtotal ?? subtotal;
  const serverShipping = quote ? quote.shipping : shipping;
  const remainingForFree = quote ? quote.amount_to_free_shipping : Math.max(0, FREE_SHIPPING_THRESHOLD - freeShipEligibleSubtotal);

  // Optional early tax estimate (state + zip) via /api/cart/tax-preview.
  const [taxState, setTaxState] = useState("");
  const [taxZip, setTaxZip] = useState("");
  const [taxPreview, setTaxPreview] = useState(null);
  useEffect(() => {
    if (items.length === 0 || isPickup || !taxState.trim() || !taxZip.trim()) { setTaxPreview(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.post("/cart/tax-preview", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.customization?.size || null })),
        address: { state: taxState, postal_code: taxZip, country: "US" },
      }).then((r) => { if (!cancelled) setTaxPreview(r.data); }).catch(() => { if (!cancelled) setTaxPreview(null); });
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [items, isPickup, taxState, taxZip]);
  const estTax = !isPickup && taxPreview?.available && !taxPreview?.exempt ? (taxPreview.tax_amount || 0) : 0;

  const total = serverSubtotal + serverShipping + (tip || 0) + estTax;
  const sizeRuns = buildSizeRuns(items);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)]">YOUR ORDER</div>
        <h1 className="font-serif-display text-5xl text-[var(--navy)] mt-3">Cart&apos;s empty, friend.</h1>
        <p className="mt-4 text-[var(--ink-soft)]">Nothing in here yet. Go pick out a tee or tumbler.</p>
        <Link to="/shop" className="btn-primary mt-8 inline-flex" data-testid="cart-shop-link">Browse the Shop</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12" data-testid="cart-page">
      <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)]">YOUR ORDER</div>
      <h1 className="font-serif-display text-5xl text-[var(--navy)] mt-2">Cart</h1>

      <div className="grid lg:grid-cols-3 gap-10 mt-10">
        <div className="lg:col-span-2 space-y-4">
          {sizeRuns.length > 0 && (
            <div className="card-rustic p-5" data-testid="cart-size-runs">
              <div className="flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--crimson)]">
                <Layers className="w-3.5 h-3.5"/> Size Runs
              </div>
              <div className="text-[11px] text-[var(--ink-soft)] mt-1 mb-3">Quick breakdown for packing — each design and the sizes ordered.</div>
              <div className="space-y-3">
                {sizeRuns.map((run) => (
                  <div key={run.key} data-testid="cart-size-run-item" className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-[rgba(27,42,65,0.08)] pb-3 last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <span className="font-serif-display text-lg text-[var(--navy)]">{run.product_name}</span>
                      {run.descriptor && <span className="font-mono-ui text-[10px] tracking-[0.15em] uppercase text-[var(--ink-soft)] ml-2">{run.descriptor}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {run.sizes.map((s) => (
                        <span key={s.size} className="inline-flex items-baseline gap-1 bg-[var(--bone-2)] rounded-sm px-2 py-1 font-mono-ui text-[11px] text-[var(--navy)]">
                          <span className="tracking-[0.1em]">{s.size}</span>
                          <span className="text-[var(--crimson)] font-bold">×{s.qty}</span>
                        </span>
                      ))}
                      <span className="font-mono-ui text-[10px] tracking-[0.15em] uppercase text-[var(--ink-soft)] ml-1">{run.totalQty} pcs</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {items.map((it) => (
            <div key={it.line_id} className="card-rustic p-5 flex gap-5" data-testid={`cart-item-${it.line_id}`}>
              <div className="w-28 h-28 rounded-sm overflow-hidden relative bg-[var(--bone-2)] flex-shrink-0">
                <img src={imageUrl(it.mockup_image)} alt="" className="absolute inset-0 w-full h-full object-cover"/>
                {it.customization?.color_hex && (
                  <div className="absolute inset-0 mix-blend-multiply" style={{ backgroundColor: it.customization.color_hex, opacity: 0.55 }}/>
                )}
                {it.customization?.layers?.length ? (
                  it.customization.layers.map((L, i) => (
                    <div
                      key={L.id || i}
                      className="absolute"
                      style={{ left: `${L.box?.x || 0}%`, top: `${L.box?.y || 0}%`, width: `${L.box?.w || 100}%`, height: `${L.box?.h || 100}%` }}
                      data-testid={`cart-item-layer-${it.line_id}-${i}`}
                    >
                      {L.type === "text" ? (
                        <div className="w-full h-full grid place-items-center text-center break-words font-serif-display leading-none" style={{ containerType: "size", color: L.color || "#F4F1EA", textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>
                          <span style={{ fontSize: "42cqh" }}>{L.text}</span>
                        </div>
                      ) : (
                        L.src && <img src={imageUrl(L.src)} alt="" className="absolute inset-0 w-full h-full object-contain" />
                      )}
                    </div>
                  ))
                ) : it.customization?.design_transform ? (
                  <div
                    className="absolute"
                    style={{
                      left: `${it.customization.design_transform.x}%`,
                      top: `${it.customization.design_transform.y}%`,
                      width: `${it.customization.design_transform.w}%`,
                      height: `${it.customization.design_transform.h}%`,
                    }}
                    data-testid={`cart-item-design-${it.line_id}`}
                  >
                    {it.customization.custom_image_data && (
                      <img src={it.customization.custom_image_data} alt="" className="absolute inset-0 w-full h-full object-contain"/>
                    )}
                    {it.customization.preset_image && (
                      <img src={imageUrl(it.customization.preset_image)} alt="" className="absolute inset-0 w-full h-full object-contain"/>
                    )}
                    {it.customization.custom_text && (
                      <div className="absolute inset-0 grid place-items-center text-center font-serif-display leading-none break-words" style={{ color: it.customization.text_color || "#F4F1EA", fontSize: "9px", textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>
                        {it.customization.custom_text}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-serif-display text-2xl text-[var(--navy)] leading-tight">{it.product_name}</div>
                    <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)] mt-1">
                      {[
                        it.customization?.color_name,
                        it.customization?.size,
                        it.customization?.preset_name,
                        it.customization?.custom_text ? `"${it.customization.custom_text}"` : null,
                        it.customization?.custom_image_data ? "Custom Upload" : null,
                        it.customization?.design_type === "none" ? "As shown" : null,
                      ].filter(Boolean).join(" · ")}
                    </div>
                    {(it.exclude_from_discounts || it.exclude_from_free_shipping) && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {it.exclude_from_discounts && (
                          <span className="inline-block bg-[var(--crimson)]/10 text-[var(--crimson)] px-2 py-0.5 rounded-sm font-mono-ui text-[8px] tracking-[0.15em] uppercase" data-testid={`cart-no-discount-${it.line_id}`}>No Discounts</span>
                        )}
                        {it.exclude_from_free_shipping && (
                          <span className="inline-block bg-[var(--navy)]/10 text-[var(--navy)] px-2 py-0.5 rounded-sm font-mono-ui text-[8px] tracking-[0.15em] uppercase" data-testid={`cart-no-freeship-${it.line_id}`}>Shipping Applies</span>
                        )}
                      </div>
                    )}
                    {it.customization?.note && (
                      <div className="mt-2 text-xs text-[var(--navy)] bg-[var(--bone-2)] border-l-2 border-[var(--gold)] rounded-sm px-2 py-1.5" data-testid={`cart-item-note-${it.line_id}`}>
                        📝 {it.customization.note}
                      </div>
                    )}
                    {it.customization?.layers?.length > 0 && (
                      <button
                        onClick={() => downloadDesign(it.customization.layers, `${(it.product_name || "design").toLowerCase().replace(/\s+/g, "-")}-${it.line_id.slice(0, 6)}.png`)}
                        data-testid={`cart-download-design-${it.line_id}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-[var(--navy)] hover:text-[var(--crimson)] font-mono-ui text-[10px] tracking-[0.15em] uppercase"
                      >
                        <Download className="w-3.5 h-3.5" /> Download design (PNG)
                      </button>
                    )}
                  </div>
                  <button onClick={() => removeItem(it.line_id)} className="text-[var(--ink-soft)] hover:text-[var(--crimson)] p-1" data-testid={`cart-remove-${it.line_id}`}>
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center border border-[rgba(27,42,65,0.2)] rounded-sm">
                    <button onClick={() => updateQuantity(it.line_id, it.quantity - 1)} className="px-3 py-1.5 text-[var(--navy)]" data-testid="cart-qty-minus">−</button>
                    <input
                      type="number"
                      min="1"
                      value={it.quantity}
                      onChange={(e) => updateQuantity(it.line_id, parseInt(e.target.value, 10) || 1)}
                      className="px-1 font-mono-ui text-sm w-14 text-center bg-transparent border-x border-[rgba(27,42,65,0.2)] py-1.5 focus:outline-none"
                      data-testid="cart-qty-input"
                    />
                    <button onClick={() => updateQuantity(it.line_id, it.quantity + 1)} className="px-3 py-1.5 text-[var(--navy)]" data-testid="cart-qty-plus">+</button>
                  </div>
                  <div className="font-serif-display text-2xl text-[var(--crimson)]">${(it.unit_price * it.quantity).toFixed(2)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card-rustic p-6 h-fit lg:sticky lg:top-28">
          <div className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[var(--navy)] mb-4">Order Summary</div>
          <div className="flex justify-between py-2 text-[var(--ink-soft)]"><span>Subtotal</span><span className="font-mono-ui" data-testid="cart-subtotal">${serverSubtotal.toFixed(2)}</span></div>
          <div className="flex justify-between py-2 text-[var(--ink-soft)]">
            <span>{isPickup ? "Shipping (Local Pickup)" : "Shipping"}</span>
            <span className="font-mono-ui" data-testid="cart-shipping">{isPickup ? "PICKUP · $0.00" : serverShipping === 0 ? "FREE" : `$${serverShipping.toFixed(2)}`}</span>
          </div>

          {/* LOCAL PICKUP */}
          <label className="flex items-start gap-2.5 mt-2 cursor-pointer select-none" data-testid="cart-pickup-toggle">
            <input
              type="checkbox"
              checked={isPickup}
              onChange={(e) => setIsPickup(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[var(--crimson)] cursor-pointer"
              data-testid="cart-pickup-checkbox"
            />
            <span className="flex-1">
              <span className="flex items-center gap-1.5 text-sm text-[var(--navy)] font-medium">
                <Store className="w-3.5 h-3.5 text-[var(--crimson)]"/> I&apos;ll pick this up at the shop (no shipping)
              </span>
              <span className="block text-[11px] text-[var(--ink-soft)] mt-0.5">Skip delivery fees — grab your order in person.</span>
            </span>
          </label>

          {!isPickup && (remainingForFree > 0 ? (
            <div className="text-[11px] text-[var(--ink-soft)] bg-[var(--bone-2)] rounded-sm px-3 py-2 mt-1" data-testid="cart-free-shipping-note">
              Add <span className="text-[var(--crimson)] font-semibold">${remainingForFree.toFixed(2)}</span> more for FREE shipping (orders over ${FREE_SHIPPING_THRESHOLD}).
            </div>
          ) : (
            <div className="text-[11px] text-[var(--navy)] bg-[var(--gold)]/20 rounded-sm px-3 py-2 mt-1" data-testid="cart-free-shipping-earned">
              🎉 You&apos;ve earned FREE shipping!
            </div>
          ))}

          {/* EARLY TAX ESTIMATE */}
          {!isPickup && (
            <div className="mt-3 pt-3 border-t border-[rgba(27,42,65,0.1)]" data-testid="cart-tax-estimator">
              <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--navy)] mb-2">Estimate Sales Tax</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={taxState}
                  onChange={(e) => setTaxState(e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="State"
                  className="input-field w-20 text-sm"
                  data-testid="cart-tax-state"
                />
                <input
                  type="text"
                  value={taxZip}
                  onChange={(e) => setTaxZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                  placeholder="Zip"
                  className="input-field flex-1 text-sm"
                  data-testid="cart-tax-zip"
                />
              </div>
              {estTax > 0 && (
                <div className="flex justify-between py-2 text-[var(--ink-soft)] mt-1">
                  <span>Est. sales tax</span>
                  <span className="font-mono-ui" data-testid="cart-tax-estimate">${estTax.toFixed(2)}</span>
                </div>
              )}
              {taxPreview?.exempt && (
                <div className="flex justify-between py-2 text-[var(--ink-soft)] mt-1">
                  <span>Est. sales tax</span>
                  <span className="font-mono-ui" data-testid="cart-tax-estimate">Exempt</span>
                </div>
              )}
              <div className="text-[10px] text-[var(--ink-soft)]/80 mt-1">Estimate only — final tax is calculated at payment.</div>
            </div>
          )}

          {/* TIP */}
          <div className="mt-4 pt-4 border-t border-[rgba(27,42,65,0.1)]" data-testid="cart-tip-section">
            <div className="flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--navy)]">
              <Heart className="w-3.5 h-3.5 text-[var(--crimson)]"/> Add a Tip
            </div>
            <p className="text-[11px] text-[var(--ink-soft)] mt-1.5 leading-relaxed">For MawMaw&apos;s grandkid spoilage fund. 100% optional, 100% appreciated. 😄</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" onClick={() => setTip(0)} data-testid="cart-tip-0"
                className={`px-3 py-2 rounded-sm border font-mono-ui text-xs transition ${(!tip || tip === 0) ? "bg-[var(--navy)] text-[var(--bone)] border-[var(--navy)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)]"}`}>
                No tip
              </button>
              {TIP_OPTIONS.map((amt) => (
                <button type="button" key={amt} onClick={() => setTip(amt)} data-testid={`cart-tip-${amt}`}
                  className={`px-4 py-2 rounded-sm border font-mono-ui text-xs transition ${tip === amt ? "bg-[var(--navy)] text-[var(--bone)] border-[var(--navy)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)] hover:border-[var(--navy)]"}`}>
                  ${amt}
                </button>
              ))}
              <div className="flex items-center border border-[rgba(27,42,65,0.2)] rounded-sm px-2">
                <span className="text-[var(--ink-soft)] text-sm">$</span>
                <input
                  type="number" min="0" step="1"
                  value={tip && !TIP_OPTIONS.includes(tip) ? tip : ""}
                  onChange={(e) => setTip(Math.max(0, parseFloat(e.target.value) || 0))}
                  placeholder="Custom"
                  className="w-20 bg-transparent outline-none py-2 text-sm font-mono-ui"
                  data-testid="cart-tip-custom"
                />
              </div>
            </div>
          </div>

          {tip > 0 && (
            <div className="flex justify-between py-2 text-[var(--ink-soft)] mt-2"><span>Tip 💛</span><span className="font-mono-ui" data-testid="cart-tip-amount">${Number(tip).toFixed(2)}</span></div>
          )}
          <div className="border-t border-[rgba(27,42,65,0.1)] mt-3 pt-3 flex justify-between items-baseline">
            <span className="font-serif-display text-xl text-[var(--navy)]">Total</span>
            <span className="font-serif-display text-3xl text-[var(--crimson)]" data-testid="cart-total">${total.toFixed(2)}</span>
          </div>
          <button onClick={() => nav("/checkout")} className="btn-primary btn-crimson w-full justify-center mt-6" data-testid="cart-checkout-button">
            Proceed to Checkout <ArrowRight className="w-4 h-4"/>
          </button>
          <Link to="/shop" className="block text-center mt-3 font-mono-ui text-[10px] tracking-[0.25em] text-[var(--navy)]">CONTINUE SHOPPING</Link>
        </div>
      </div>
    </div>
  );
}
